import { type DungeonView, floorsCleared, type RunEnd, starsFor } from '@xox/shared'
import { Sparkles } from 'lucide-react'
import { BoardOverlay } from '../components/BoardOverlay'
import { Button, LinkButton, Stars } from '../components/ui'
import { Confetti } from '../ice/art/Confetti'
import { ordinal, plural } from '../lib/format'

interface Props {
  view: DungeonView
  end: RunEnd | null
  onPeek: () => void
  onPractice: () => void
}

/** The card over the board once a run is over: outcome, stars, score, XP and the way onward. */
export function RunEndOverlay({ view, end, onPeek, onPractice }: Props) {
  const won = view.status === 'won'
  const stars = starsFor(view)
  return (
    <BoardOverlay wide onPeek={onPeek}>
      {won && <Confetti />}
      <div
        className={`text-xs font-black uppercase tracking-[0.25em] ${won ? 'text-emerald-600 dark:text-emerald-400' : view.status === 'abandoned' ? 'text-slate-500' : 'text-rose-600 dark:text-rose-400'}`}
      >
        {won ? 'Victory' : view.status === 'abandoned' ? 'Run ended' : 'You died'}
      </div>
      <div className="mt-1 text-2xl font-black leading-tight">
        {won ? 'The Lich is dust.' : `Floor ${view.floor}`}
      </div>
      <div className="mt-2 flex justify-center">
        <Stars earned={stars} size="lg" />
      </div>
      <div className="mt-2 text-3xl font-black tabular-nums">{end?.score ?? view.score} pts</div>
      <p className="text-xs text-slate-500">
        {plural(floorsCleared(view), 'floor')} cleared · {plural(view.kills, 'kill')} · {view.gold}{' '}
        gold
      </p>
      {end && (
        <div className="mt-3 flex flex-col items-center gap-1 text-sm">
          <span className="inline-flex items-center gap-1 font-semibold text-sky-600 dark:text-sky-400">
            <Sparkles className="size-4" aria-hidden /> +{end.xpGained} XP
          </span>
          {end.levelAfter > end.levelBefore && (
            <span className="font-black text-emerald-600 dark:text-emerald-400">
              Account level {end.levelAfter}!
            </span>
          )}
          {end.rank !== null && (
            <span className="text-slate-500">{ordinal(end.rank)} on today's board</span>
          )}
        </div>
      )}
      <div className="mt-4 flex flex-col gap-2">
        <LinkButton to="/dungeon" variant="primary" className="w-full">
          Back to the dungeon
        </LinkButton>
        <Button variant="secondary" className="w-full" onClick={onPractice}>
          Practice run
        </Button>
      </div>
    </BoardOverlay>
  )
}
